import React, { useState, useEffect, useCallback } from "react";
import { BaseModal } from "../ui/BaseModal.tsx";
import { FileList } from "../ui/FileList.tsx";
import { ActionButton } from "../ui/ActionButton.tsx";
import { Box, Button, Typography } from "@mui/material";
import { Plus, Trash, ArrowLeft, Upload } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { CustomToggleTabs } from "../ui/CustomToggleTabs.tsx";
import {
  getUserDocuments,
  uploadMultipleFiles,
  deleteFile,
} from "../../services/uploadFilesApi";
import { CustomAlert } from "../ui/CustomAlert.tsx";
import { CustomTextField } from "../ui/CustomTextField.tsx";

interface Collection {
  files?: any[];
  [key: string]: any;
}

interface FileManagementModalProps {
  onClose: () => void;
  isOpen: boolean;
}

export default function FileManagementModal({
  onClose,
  isOpen,
}: FileManagementModalProps) {
  const [collections, setCollections] = useState<{ [key: string]: Collection }>(
    {}
  );
  const [newCollectionName, setNewCollectionName] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [defaultCollection, setDefaultCollection] = useState("");
  const [textConfig, setTextConfig] = useState<any>({});

  const [alertConfig, setAlertConfig] = useState<{
    open: boolean;
    type: "loading" | "success" | "error" | "confirm";
    message: string;
    title?: string;
    onConfirm?: () => Promise<void>;
  }>({
    open: false,
    type: "loading",
    message: "",
    title: "",
    onConfirm: undefined,
  });

  const handleCloseAlert = useCallback(() => {
    setAlertConfig((prev) => ({ ...prev, open: false }));
  }, []);

  const fetchUserDocuments = useCallback(async () => {
    setIsLoading(true);
    setAlertConfig({
      open: true,
      type: "loading",
      message: "Cargando documentos...",
    });

    try {
      const documents = await getUserDocuments();
      const groupedDocuments = documents.reduce((acc: any, doc: any) => {
        const paths = decodeURIComponent(doc.FILE_NAME).split("/");
        let currentLevel = acc;

        paths.forEach((path: string, index: number) => {
          if (index === paths.length - 1) {
            if (!currentLevel.files) currentLevel.files = [];
            currentLevel.files.push(doc);
          } else {
            if (!currentLevel[path]) currentLevel[path] = { files: [] };
            currentLevel = currentLevel[path];
          }
        });

        return acc;
      }, {});

      if (!groupedDocuments[defaultCollection]) {
        groupedDocuments[defaultCollection] = { files: [] };
      }

      setCollections(groupedDocuments);
      handleCloseAlert();
    } catch (error) {
      setAlertConfig({
        open: true,
        type: "error",
        message:
          textConfig.FILE_MANAGEMENT_ERROR_UPLOAD_FILE ||
          "Error al cargar los documentos",
      });
    } finally {
      setIsLoading(false);
    }
  }, [defaultCollection, handleCloseAlert, textConfig]);

  const fetchInitialData = async () => {
    try {
      const configResponse = await fetch("/configuration.json");
      const configData = await configResponse.json();
      setDefaultCollection(configData.DEFAULT_COLLECTION);

      const textResponse = await fetch("/text.json");
      const textData = await textResponse.json();
      setTextConfig(textData);
    } catch (error) {
      setAlertConfig({
        open: true,
        type: "error",
        message: "Error al cargar la configuración inicial",
      });
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (defaultCollection) {
      fetchUserDocuments();
    }
  }, [defaultCollection, fetchUserDocuments]);

  const handleFileSelection = (files: File[]) => {
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const handleUploadFiles = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    setAlertConfig({
      open: true,
      type: "loading",
      message: "Subiendo archivos...",
    });

    try {
      const currentFolder = currentPath[currentPath.length - 1] || "";
      const filesWithPath = selectedFiles.map((file) => {
        return new File([file], file.name, { type: file.type });
      });

      await uploadMultipleFiles(filesWithPath, currentFolder);
      setAlertConfig({
        open: true,
        type: "success",
        message:
          textConfig.FILE_MANAGEMENT_FILE_UPLOADING ||
          "Archivos subidos exitosamente",
      });

      await fetchUserDocuments();
      setSelectedFiles([]);
    } catch (error) {
      setAlertConfig({
        open: true,
        type: "error",
        message:
          textConfig.FILE_MANAGEMENT_ERROR_UPLOAD_FILE ||
          "Error al subir los archivos",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFolderClick = (folderName: string) => {
    setCurrentPath((prev) => [...prev, folderName]);
    setSelectedFiles([]);
  };

  const handleNavigateBack = () => {
    setCurrentPath((prev) => prev.slice(0, -1));
    setSelectedFiles([]);
  };

  const handleNavigateToRoot = () => {
    setCurrentPath([]);
    setSelectedFiles([]);
  };

  const handleCreateCollection = () => {
    if (newCollectionName.trim() === "") {
      setAlertConfig({
        open: true,
        type: "error",
        message:
          textConfig.FILE_MANAGEMENT_COLLECTION_ERROR_EMPTY ||
          "El nombre de la colección no puede estar vacío",
      });
      return;
    }

    const updatedCollections = { ...collections };
    if (currentPath.length === 0) {
      updatedCollections[newCollectionName] = { files: [] };
    } else {
      let current = updatedCollections;
      for (const folder of currentPath.slice(0, -1)) {
        current = current[folder];
      }
      current[currentPath[currentPath.length - 1]][newCollectionName] = {
        files: [],
      };
    }

    setCollections(updatedCollections);
    setNewCollectionName("");
    setAlertConfig({
      open: true,
      type: "success",
      message: "Colección creada exitosamente",
    });
  };

  const handleDeleteFile = async (fileHash: string, collectionName: string) => {
    setAlertConfig({
      open: true,
      type: "confirm",
      title: "¿Eliminar archivo?",
      message: "¿Está seguro que desea eliminar este archivo?",
      onConfirm: async () => {
        setAlertConfig({
          open: true,
          type: "loading",
          message:
            textConfig.FILE_MANAGEMENT_DELETING_FILE || "Eliminando archivo...",
        });

        try {
          await deleteFile(fileHash, collectionName);
          setAlertConfig({
            open: true,
            type: "success",
            message:
              textConfig.FILE_MANAGEMENT_DELETE_FILE ||
              "Archivo eliminado exitosamente",
          });
          await fetchUserDocuments();
        } catch (error) {
          setAlertConfig({
            open: true,
            type: "error",
            message:
              textConfig.FILE_MANAGEMENT_ERROR_DELETE_FILE ||
              "Error al eliminar el archivo",
          });
        }
      },
    });
  };

  const getCurrentDirectory = () => {
    let current = collections;
    for (const folder of currentPath) {
      if (current[folder]) {
        current = current[folder];
      } else {
        return { files: [] };
      }
    }
    return current;
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileSelection,
    accept: {
      "application/pdf": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
      "text/plain": [],
    },
    disabled: currentPath.length === 0,
  });

  const currentItems = [
    ...Object.entries(getCurrentDirectory())
      .filter(([key]) => key !== "files")
      .map(([name]) => ({
        name,
        type: "folder" as const,
        onClick: () => handleFolderClick(name),
      })),
    ...(getCurrentDirectory().files?.map((file: any) => ({
      name: decodeURIComponent(file.FILE_NAME).split("/").pop() || "",
      type: "file" as const,
      hash: file.FILE_HASH,
      collection: file.COLLECTION_NAME,
    })) || []),
  ];

  return (
    <>
      <BaseModal
        title="Administración de Archivos"
        onClose={onClose}
        isOpen={isOpen}
      >
        <CustomToggleTabs
          value={viewMode}
          onChange={(newValue) => setViewMode(newValue)}
        />

        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Button
            variant="text"
            onClick={handleNavigateToRoot}
            sx={{ color: "#fff" }}
          >
            Raíz
          </Button>
          {currentPath.map((folder, index) => (
            <React.Fragment key={folder}>
              <Typography color="white">/</Typography>
              <Button
                variant="text"
                onClick={() => setCurrentPath(currentPath.slice(0, index + 1))}
                sx={{ color: "#fff" }}
              >
                {folder}
              </Button>
            </React.Fragment>
          ))}
        </Box>

        {currentPath.length > 0 && (
          <Button
            onClick={handleNavigateBack}
            startIcon={<ArrowLeft />}
            sx={{ mb: 2 }}
            variant="outlined"
          >
            Volver
          </Button>
        )}

        <Box
          sx={{
            height: "200px",
            border: "1px solid #2D2D2D",
            borderRadius: 2,
            p: 2,
            mb: 2,
            backgroundColor: "#18181b",
            overflowY: "auto",
          }}
        >
          {isLoading ? (
            <Typography color="white">Cargando...</Typography>
          ) : (
            <FileList
              items={currentItems}
              onDelete={(name) => {
                const item = currentItems.find((i) => i.name === name);
                if (item && "hash" in item) {
                  handleDeleteFile(item.hash, item.collection);
                }
              }}
              viewMode={viewMode}
            />
          )}
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={2}
          mb={2}
          sx={{
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <CustomTextField
            placeholder={
              currentPath.length === 0
                ? "Nombre de la nueva colección"
                : "Nombre de la nueva carpeta"
            }
            value={newCollectionName}
            onChange={(e) => setNewCollectionName(e.target.value)}
          />
          <ActionButton
            label={
              currentPath.length === 0 ? "Crear Colección" : "Crear Carpeta"
            }
            onClick={handleCreateCollection}
            Icon={<Plus />}
          />
        </Box>

        {currentPath.length > 0 && (
          <>
            <div
              {...getRootProps()}
              className="border-dashed border-2 p-4 rounded-lg cursor-pointer"
            >
              <input {...getInputProps()} />
              {isDragActive ? (
                <p className="text-green-400 text-center">
                  Suelta los archivos aquí...
                </p>
              ) : (
                <p className="text-gray-400 text-center">
                  Arrastra y suelta archivos aquí, o haz clic para
                  seleccionarlos
                </p>
              )}
            </div>

            {selectedFiles.length > 0 && (
              <Box mt={2} p={2} bgcolor="#27272a" borderRadius={2}>
                <Typography color="#fff" variant="body2" mb={2}>
                  Archivos Seleccionados:
                </Typography>
                <ul>
                  {selectedFiles.map((file, index) => (
                    <li key={index} className="flex justify-between text-white">
                      {file.name} - {(file.size / 1024).toFixed(2)} KB
                      <Trash
                        size={16}
                        className="text-red-500 cursor-pointer"
                        onClick={() =>
                          setSelectedFiles((prev) =>
                            prev.filter((_, i) => i !== index)
                          )
                        }
                      />
                    </li>
                  ))}
                </ul>
              </Box>
            )}
          </>
        )}

        <Box display="flex" mt={2}>
          <Box flex={1} display="flex" justifyContent="flex-start">
            {currentPath.length > 0 && (
              <Button
                variant="outlined"
                startIcon={<Upload size={16} />}
                onClick={handleUploadFiles}
                disabled={isUploading || selectedFiles.length === 0}
                sx={{
                  backgroundColor: "#27272A",
                  borderColor: "#515151",
                  color: "#FFFFFF",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: "#363639",
                    borderColor: "#515151",
                  },
                  "&:disabled": {
                    backgroundColor: "#1F1F1F",
                    borderColor: "#383838",
                    color: "#666666",
                  },
                }}
              >
                Subir Archivos
              </Button>
            )}
          </Box>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              backgroundColor: "#FF3D3D",
              "&:hover": {
                backgroundColor: "#FF5252",
              },
            }}
          >
            Cerrar
          </Button>
        </Box>
      </BaseModal>
      <CustomAlert
        open={alertConfig.open}
        type={alertConfig.type}
        message={alertConfig.message}
        title={alertConfig.title}
        onClose={handleCloseAlert}
        onConfirm={async () => {
          if (alertConfig.onConfirm) {
            await alertConfig.onConfirm();
          }
        }}
      />
    </>
  );
}
